import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/joy/styles";
import { List, ListItem } from "@mui/joy";
import Modal, { ModalProps, modalClasses } from "@mui/joy/Modal";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import Dropdown from "@mui/joy/Dropdown";
import IconButton from "@mui/joy/IconButton";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import ModalClose from "@mui/joy/ModalClose";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import LogoutIcon from "@mui/icons-material/Logout";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import { useLogoutFunction } from "@propelauth/react";
import { useUser } from "../graphql/user";
import { User } from "../models/user";
// import { ColorMode } from "./colorMode";
import { routes } from "../pages/routes";
import { stringToColor } from "../util/common";

export interface DrawerProps extends Omit<ModalProps, "children"> {
  children: React.ReactNode
  title: string
  size?: number | string
  position?: "left" | "right" | "top" | "bottom"
}

export function Drawer({
  children,
  title = "Title",
  position = "left",
  size,
  sx,
  ...props
}: DrawerProps) {
  return (
    <Modal
      keepMounted
      sx={[
        {
          transitionProperty: "visibility",
          transitionDelay: props.open ? "0s" : "300ms",
          [`& .${modalClasses.backdrop}`]: {
            opacity: props.open ? 1 : 0,
            transition: "opacity 0.3s ease"
          }
        },
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
      {...props}
    >
      <Sheet
        sx={{
          px: 2,
          py: 1.5,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          overflow: "auto",
          ...(position === "left" && {
            left: 0,
            transform: props.open ? "translateX(0)" : "translateX(-100%)"
          }),
          ...(position === "right" && {
            right: 0,
            transform: props.open ? "translateX(0)" : "translateX(100%)"
          }),
          ...(position === "top" && {
            top: 0,
            transform: props.open ? "translateY(0)" : "translateY(-100%)"
          }),
          ...(position === "bottom" && {
            bottom: 0,
            transform: props.open ? "translateY(0)" : "translateY(100%)"
          }),
          height: position.match(/(left|right)/) ? "100%" : size,
          width: position.match(/(top|bottom)/) ? "100vw" : size,
          boxShadow: "md",
          transition: "transform 0.3s ease"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography fontSize="lg" fontWeight="lg" sx={{ flex: 1 }}>
            {title}
          </Typography>
          <ModalClose sx={{ position: "initial" }} />
        </Box>
        <Divider sx={{ mt: 1, mb: 1.5 }} />
        {children}
      </Sheet>
    </Modal>
  );
}

export function NavBar() {
  const logoutFn = useLogoutFunction();
  const navigate = useNavigate();
  const theme = useTheme();
  const bodyBgColor = useMemo(() => theme.palette.background.body, [theme.palette.background.body]);

  const [open, setOpen] = useState(false);
  const { user } = useUser();
  const { firstName, lastName } = user as User;

  return useMemo(() => {
    const onNavClick = (route: string) => {
      setOpen(false);
      navigate(route);
    };

    const navButtons = [
      routes.home,
      routes.chargerList,
      routes.vehicleList,
      // Stuffing in the username is kinda hacky
      {...routes.profile, name: firstName ?? ""},
    ];
  
    const children = (
      <>
        <List style={listBodyStyle}>
          {navButtons.map(nb =>  {
            return (
              <React.Fragment key={nb.name}>
                {/* separator  */}
                { nb.end && <Box sx={{flex:1}} /> }

                <ListItem style={{...listItemStyle}}>
                  <Button
                    color="primary"
                    onClick={() => onNavClick(nb.route)}
                    startDecorator={<nb.icon/>}
                    style={buttonStyle}
                    variant="plain"
                  >
                    {nb.name}
                  </Button>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>
      </>
    );

    function stringAvatar(name: string) {
      return {
        sx: {
          alignSelf: "center",
          bgcolor: stringToColor(name),
          color: "white",
          size: "sm",
        },
        children: `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
      };
    }

    return (
      <>
        <AppBar style={{ ...appBarStyle, backgroundColor: bodyBgColor }}>
          <Box style={toolbarStyle}>
            <IconButton
              onClick={() => setOpen(true)}
              variant="plain"
            >
              <MenuIcon />
            </IconButton>

            {/* Middle separator  */}
            <div
              onClick={() => navigate(routes.home.route)}
              style={titleTextStyle}
            />

            <Box sx={{ display: "flex" }}>
              {/* <ColorMode /> */}
              <Dropdown>
                <MenuButton variant="plain" style={menuButtonStyle}>
                  <Avatar {...stringAvatar(`${firstName} ${lastName}`)} />
                </MenuButton>
                <Menu>
                  <MenuItem>
                    <Button
                      color="neutral"
                      onClick={() => navigate(routes.profile.route)}
                      startDecorator={<PermIdentityIcon />}
                      style={buttonStyle}
                      variant="plain"
                    >
                      Profile
                    </Button>
                  </MenuItem>
                  <MenuItem>
                    <Button
                      color="neutral"
                      onClick={() => logoutFn(true)}
                      startDecorator={<LogoutIcon />}
                      style={buttonStyle}
                      variant="plain"
                    >
                      Sign Out
                    </Button>
                  </MenuItem>
                </Menu>
              </Dropdown>
            </Box>
          </Box>
        </AppBar>

        <Drawer
          title="MoCha"
          children={children}
          onClose={() => setOpen(false)}
          open={open}
        />
      </>
    );
  }, [bodyBgColor, firstName, lastName, logoutFn, navigate, open]);
}

// Styles
const appBarStyle = {
  boxShadow: "none",
};

const listBodyStyle = {
  height: "auto",
};

const listItemStyle = {
  paddingLeft: 0,
};

const buttonStyle = {
  width: "100%",
  justifyContent: "flex-start",
  paddingLeft: 0,
};

const menuButtonStyle = {
  paddingLeft: 0,
  paddingRight: "6px",
};

const toolbarStyle = {
  alignItems: "center",
  display: "flex",
  flex: 1,
  justifyContent: "space-between",
};

const titleTextStyle = {
  background: "url('/mocha-logo-sm.png') no-repeat center",
  backgroundSize: "contain",
  cursor: "pointer",
  flex: 1,
  height: "1.5em",
};